/**
 * MODULE: assistantConnectorsApi.ts
 * PURPOSE: List Langflow flows and MCP tools for assistant connector picker.
 */

import { getApiBaseUrl, getAuthHeaders } from '@shared/lib/frontend/api/config';
import type { AssistantConnectorItem } from './assistantConnectorTypes';

type LangflowFlowsListResponse = {
  flows?: Array<{ id?: string; name?: string | null; description?: string | null }>;
};

type McpManifestResponse = {
  tools?: Array<{ name?: string; description?: string | null }>;
};

const WEB_CONNECTOR: AssistantConnectorItem = {
  id: 'web',
  kind: 'web',
  label: 'Web',
  description: 'Search the web for live information',
};

export async function fetchLangflowFlowConnectors(): Promise<AssistantConnectorItem[]> {
  const url = `${getApiBaseUrl().replace(/\/$/, '')}/api/v1/platform/langflow/flows`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  const raw = await res.text();
  if (!res.ok) {
    throw new Error(`Langflow flows list failed: HTTP ${res.status}`);
  }
  let data: LangflowFlowsListResponse = {};
  try {
    data = raw ? (JSON.parse(raw) as LangflowFlowsListResponse) : {};
  } catch {
    throw new Error('Langflow flows list: invalid JSON');
  }
  const flows = Array.isArray(data.flows) ? data.flows : [];
  return flows
    .filter((f) => f.id && (f.name ?? '').trim())
    .map((f) => ({
      id: `flow:${f.id}`,
      kind: 'langflow_flow' as const,
      label: String(f.name).trim(),
      description: (f.description ?? '').trim() || 'Langflow workflow',
      flowId: String(f.id).trim(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export async function fetchMcpToolConnectors(): Promise<AssistantConnectorItem[]> {
  const url = `${getApiBaseUrl().replace(/\/$/, '')}/mcp/.well-known/mcp.json`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  const raw = await res.text();
  if (!res.ok) {
    throw new Error(`MCP manifest failed: HTTP ${res.status}`);
  }
  let data: McpManifestResponse = {};
  try {
    data = raw ? (JSON.parse(raw) as McpManifestResponse) : {};
  } catch {
    throw new Error('MCP manifest: invalid JSON');
  }
  const tools = Array.isArray(data.tools) ? data.tools : [];
  return tools
    .filter((t) => (t.name ?? '').trim())
    .map((t) => ({
      id: `mcp:${t.name}`,
      kind: 'mcp_tool' as const,
      label: String(t.name).trim(),
      description: (t.description ?? '').trim() || 'Brazn MCP tool',
      mcpTool: String(t.name).trim(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export async function fetchAssistantConnectorCatalog(): Promise<AssistantConnectorItem[]> {
  const [flows, mcpTools] = await Promise.allSettled([
    fetchLangflowFlowConnectors(),
    fetchMcpToolConnectors(),
  ]);
  const flowItems = flows.status === 'fulfilled' ? flows.value : [];
  const mcpItems = mcpTools.status === 'fulfilled' ? mcpTools.value : [];
  return [WEB_CONNECTOR, ...flowItems, ...mcpItems];
}
